import React, { useLayoutEffect, useRef } from 'react';
import { Box, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { makeURL } from '../../helperFunctions';

const useStyles = makeStyles(() => ({
  coverPage: {
    boxSizing: 'border-box',
    position: 'relative',
    width: 'var(--report-page-w)',
    height: 'var(--report-page-h)',
    overflow: 'hidden',
    pageBreakAfter: 'always',
    breakAfter: 'page',
    background: '#fff',
  },
  middleGrayBox: {
    position: 'absolute',
    left: '-1px',
    right: '-1px',
    top: '0',
    bottom: 'calc(var(--bottom-h-px) - 1px)',
    background: '#DADADA',
  },
  bottomNavy: {
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: 0,
    height: 'var(--bottom-h-px)',
    background: '#203C52',
  },
  coverLogo: {
    position: 'absolute',
    top: 'var(--logo-top-px)',
    left: '50%',
    transform: 'translateX(-50%)',
    width: '160px',
    height: 'auto',
  },
  coverTitle: {
    position: 'absolute',
    top: 'var(--title-top-px)',
    left: '50%',
    transform: 'translateX(-50%)',
    fontSize: '44px',
    fontWeight: 300,
    lineHeight: 1.1,
    color: '#111827',
    textAlign: 'center',
    margin: 0,
  },
  coverMetaCenter: {
    position: 'absolute',
    left: '50%',
    transform: 'translateX(-50%)',
    top: 'var(--meta-top-px)',
    fontSize: '20px',
    fontWeight: 300,
    lineHeight: 1.35,
    color: '#F8FAFC',
    textAlign: 'center',
    margin: 0,
  },
  coverFooterLeft: {
    position: 'absolute',
    left: '56px',
    bottom: '52px',
    fontSize: '14px',
    lineHeight: 1.35,
    color: '#F8FAFC',
    textAlign: 'left',
    whiteSpace: 'pre-line',
    margin: 0,
  },
  coverFooterRight: {
    position: 'absolute',
    right: '56px',
    bottom: '52px',
    fontSize: '14px',
    lineHeight: 1.35,
    color: '#F8FAFC',
    textAlign: 'right',
    whiteSpace: 'pre-line',
    margin: 0,
  },
  line: {
    fontSize: 'inherit',
    fontWeight: 'inherit',
    lineHeight: 'inherit',
    color: 'inherit',
    margin: 0,
  },
}));

const CoverPageMarketingStrategy = ({ metadata = [] }) => {
  const classes = useStyles();
  const coverRef = useRef(null);

  useLayoutEffect(() => {
    if (!coverRef.current) return;
    const pageH = Math.round(coverRef.current.getBoundingClientRect().height);
    if (!pageH) return;

    const bottomH = Math.min(pageH - 120, Math.round(pageH * 0.5) + 100);
    coverRef.current.style.setProperty('--bottom-h-px', `${bottomH}px`);

    const grayTop = 0;
    const grayBottom = pageH - bottomH;
    const grayHeight = Math.max(0, grayBottom - grayTop);
    const navyTop = pageH - bottomH;

    const grayCenter = grayTop + grayHeight / 2;
    const logoTop = Math.round(grayCenter - 128);
    const titleTop = Math.round(grayCenter - 8);
    const metaTop = Math.round(navyTop + bottomH * 0.14);

    coverRef.current.style.setProperty('--logo-top-px', `${logoTop}px`);
    coverRef.current.style.setProperty('--title-top-px', `${titleTop}px`);
    coverRef.current.style.setProperty('--meta-top-px', `${metaTop}px`);
  }, []);

  const getMeta = label => metadata.find(item => item.label === label)?.value || 'N/A';
  const getQuarterLabel = () => {
    const quarterRaw = String(getMeta('Quarter') || '').trim();
    if (!quarterRaw || quarterRaw.toUpperCase() === 'N/A') return 'N/A';
    return quarterRaw.toUpperCase().startsWith('Q') ? quarterRaw.toUpperCase() : `Q${quarterRaw}`;
  };
  const filters = metadata
    .filter(item => item.label === 'Target Market')
    .map(item => item.value || 'N/A')
    .filter(Boolean);

  return (
    <Box className={`${classes.coverPage} reportPage coverPage`} data-report-page="true" ref={coverRef}>
      <div className={classes.bottomNavy} />
      <div className={classes.middleGrayBox} />

      <img src={makeURL('/images/logos/BSV_Dark_no_background.png')} alt="BSV logo" className={classes.coverLogo} />

      <Typography className={classes.coverTitle}>Marketing Strategy{`\n`}Report</Typography>

      <Box className={classes.coverMetaCenter}>
        <Typography className={classes.line}>{`Campaign: ${getMeta('Campaign')}`}</Typography>
        <Typography className={classes.line}>{`Brand: ${getMeta('Brand')}`}</Typography>
        <Typography className={classes.line}>{`Country: ${getMeta('Country')}`}</Typography>
        <Typography className={classes.line}>{`Year: ${getMeta('Year Number')}/${getQuarterLabel()}`}</Typography>
      </Box>

      <Box className={classes.coverFooterLeft}>
        <Typography className={classes.line}>Target Market:</Typography>
        {(filters.length ? filters : ['N/A']).map((item, idx) => (
          <Typography key={`${item}-${idx}`} className={classes.line}>
            {item || 'N/A'}
          </Typography>
        ))}
      </Box>

      <Box className={classes.coverFooterRight}>
        <Typography className={classes.line}>Generated:</Typography>
        <Typography className={classes.line}>{getMeta('Generated Date')}</Typography>
        <Typography className={classes.line}>{getMeta('Generated Time')}</Typography>
      </Box>
    </Box>
  );
};

export default CoverPageMarketingStrategy;
